import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { GripHorizontal } from "lucide-react";


interface ResizableBottomPanelProps {
  isOpen: boolean;
  height: number;
  minHeight?: number;
  maxHeight?: number;
  onHeightChange: (height: number) => void;
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

export function ResizableBottomPanel({
  isOpen,
  height,
  minHeight = 150,
  maxHeight = 600,
  onHeightChange,
  header,
  children,
  footer,
}: ResizableBottomPanelProps) {
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      resizeStartY.current = e.clientY;
      resizeStartHeight.current = height;
    },
    [height]
  );

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = resizeStartY.current - e.clientY;
      const newHeight = Math.max(
        minHeight,
        Math.min(maxHeight, resizeStartHeight.current + deltaY)
      );
      onHeightChange(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, minHeight, maxHeight, onHeightChange]);

  if (!isOpen) return null;

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-[100] flex flex-col border-t border-[#333] bg-background/98 shadow-lg backdrop-blur-sm"
      style={{ height }}
    >
      <div
        onMouseDown={handleMouseDown}
        className="absolute -top-3 left-0 right-0 h-6 cursor-row-resize flex items-center justify-center z-10"
      >
        <div className="flex items-center gap-1 rounded-full border border-[#333] bg-background px-3 py-1 transition-colors hover:bg-muted/60">
          <GripHorizontal className="size-4 text-muted-foreground" />
        </div>
      </div>

      <div className="flex h-full flex-col pt-2">
        {header}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">{children}</div>
        {footer && <div className="shrink-0">{footer}</div>}
      </div>
    </div>
  );
}
